import { FieldViewModel } from "../../fields"
import { BiomassUnit, LayerTypeId } from "../../generated/enums"
import { except, groupBy, isArrayEmpty, partition } from "../../utils/array"
import { sortDates } from "../../utils/utils"
import { IDataLayer } from "../types"
import { createLayerNode, createNode, getLayerTypeFieldsNodes } from "./nodes"
import { MenuNode, NodeLayer, NodesContext } from "./types"
import { SatImageType as ImageType } from "../../generated/enums"
import { Build } from "@mui/icons-material"

export function getSatelliteRootNode<T extends NodeLayer>(context: NodesContext<T>, layers: IDataLayer[], field: FieldViewModel): MenuNode<T> {

    return createNode({
        id: "satellite",
        text: "Satellite Imagery",
        field,
        class: "type-group",
        canShow: false
    }, layers, () => getSatelliteNodeChildren(context, layers, field))


}


function getSatelliteNodeChildren<T extends NodeLayer>(context: NodesContext<T>, layers: IDataLayer[], field: FieldViewModel): MenuNode<T>[] {

    const [planet, sentinel] = partition(layers, isPlanetLayer)

    const children = get(sentinel).concat(get(planet, "Planet"))

    return children

    function formatName(name: string, source: string) {
        return source
            ? `${name} (${source})`
            : name
    }

    function get(layers: IDataLayer[], sourceName?: string) {

        const biomass = layers.filter(l => l.TypeId == LayerTypeId.Biomass)

        const evi = biomass.filter(b => b.Unit === BiomassUnit.EVI)
        const chi = biomass.filter(b => b.Unit === BiomassUnit.CHI)
        const ndvi  =except(biomass, evi.concat(chi))

        const rgb = layers.filter(l => l.TypeId == LayerTypeId.Picture)

        const getTypeNode = (layers: IDataLayer[], text: string, tooltip?: string) => {

            if (!isArrayEmpty(layers)) {

                layers = layers.sort((a, b) => sortDates(b.Date, a.Date))

                const id = text.toLocaleLowerCase()

                const getContents = () => field
                    ? getSatelliteLayers(context, layers, field)
                    : getSatelliteDateGroups(context, layers, id)

                return createNode({
                    id: `satellite-${id}`,
                    text,
                    class: "type",
                    canShow: false,
                    tooltip,
                }, layers, getContents)

            }

        }

        return [
            getTypeNode(ndvi, formatName("NDVI", sourceName)),
            getTypeNode(evi, formatName("EVI", sourceName), "Enhanced Vegetation Index"),
            getTypeNode(chi, formatName("CHI", sourceName), "Chlorophyll Index"),
            getTypeNode(rgb, formatName("RGB", sourceName), "True Colour"),
        ].filter(n => !!n)

    }
}

function getSatelliteLayers<T extends NodeLayer>(context: NodesContext<T>, layers: IDataLayer[], field: FieldViewModel): MenuNode<T>[] {

    const type = layers[0].Type

    return layers.map(l => createLayerNode(context, l, type, field))

}

function getSatelliteDateGroups<T extends NodeLayer>(context: NodesContext<T>, layers: IDataLayer[], groupId): MenuNode<T>[] {

    const type = layers[0].Type

    let groups = groupBy(layers, l => l.Date, d => d ? d.valueOf() : Number.MAX_VALUE)

    groups = groups.sort((a, b) => sortDates(b.key, a.key))

    return groups.map(g => createNode({

        id: `satellite-${groupId}-${g.key.valueOf()}`,
        text: g.key.toLocaleDateString(),
        class: "date",
        canShow: true,
    }, g.values, () => getLayerTypeFieldsNodes(context, type, g.values)))

}

function isPlanetLayer(layer: IDataLayer): boolean {

    const type = layer.SatelliteData?.type as ImageType

    switch (type) {
        case ImageType.PlanetNDVI:
        case ImageType.PlanetCHI:
        case ImageType.PlanetRGB:
            return true

        default:
            return false

    }
}
